import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSavedMessages,
  fetchSavedMessagesByChannel,
  fetchChannelsSummary,
  searchSavedMessages,
  SAVED_MESSAGES_PAGE_SIZE,
} from '../../store/slices/savedMessageSlice';
import SavedMessagesHeader from './SavedMessagesHeader';
import ChannelFilterChips from './ChannelFilterChips';
import SavedMessageList from './SavedMessageList';

const SEARCH_DEBOUNCE_MS = 350;

const SavedMessagesPanel = () => {
  const dispatch = useDispatch();
  const {
    savedMessages,
    savedMessagesByChannel,
    channelMetaByChannel,
    channelsSummary,
    listMeta,
    searchResults,
    searchMeta,
    loading,
    loadingMore,
    channelLoading,
    channelLoadingMore,
    searchLoading,
    searchLoadingMore,
    error,
  } = useSelector((state) => state.savedMessages);
  const { channels, directMessages } = useSelector((state) => state.channels);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const loadMoreLockRef = useRef(false);

  const trimmedSearch = searchQuery.trim();
  const isSearchActive = trimmedSearch.length > 0;

  const getChannelName = useCallback((channelId) => {
    const allChannels = [...channels, ...directMessages];
    const channel = allChannels.find((ch) => ch.id === channelId);
    return channel?.name || 'Unknown Channel';
  }, [channels, directMessages]);

  const loadList = useCallback(() => {
    if (selectedChannelId) {
      dispatch(
        fetchSavedMessagesByChannel({
          channelId: selectedChannelId,
          page: 0,
          size: SAVED_MESSAGES_PAGE_SIZE,
          append: false,
        })
      );
    } else {
      dispatch(
        fetchSavedMessages({
          page: 0,
          size: SAVED_MESSAGES_PAGE_SIZE,
          append: false,
        })
      );
    }
  }, [dispatch, selectedChannelId]);

  useEffect(() => {
    dispatch(fetchChannelsSummary());
  }, [dispatch]);

  useEffect(() => {
    if (isSearchActive) return;
    loadList();
  }, [isSearchActive, loadList]);

  useEffect(() => {
    if (!isSearchActive) return undefined;

    const timer = setTimeout(() => {
      dispatch(
        searchSavedMessages({
          query: trimmedSearch,
          channelId: selectedChannelId,
          page: 0,
          size: SAVED_MESSAGES_PAGE_SIZE,
          append: false,
        })
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [dispatch, trimmedSearch, selectedChannelId, isSearchActive]);

  const totalCount = useMemo(() => {
    if (channelsSummary.length > 0) {
      return channelsSummary.reduce((sum, item) => sum + (item.count || 0), 0);
    }
    return listMeta.totalElements || savedMessages.length;
  }, [channelsSummary, listMeta.totalElements, savedMessages.length]);

  const channelChips = useMemo(() => {
    return (channelsSummary || [])
      .map((item) => ({
        channelId: item.channelId,
        name: getChannelName(item.channelId),
        count: item.count || 0,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [channelsSummary, getChannelName]);

  const channelSuggestions = useMemo(() => {
    const query = trimmedSearch.toLowerCase();
    if (!query) return [];
    return channelChips
      .filter((chip) => chip.name.toLowerCase().includes(query))
      .slice(0, 8);
  }, [trimmedSearch, channelChips]);

  const activeMessages = isSearchActive
    ? searchResults
    : selectedChannelId
      ? savedMessagesByChannel[selectedChannelId] || []
      : savedMessages;

  const activeMeta = isSearchActive
    ? searchMeta
    : selectedChannelId
      ? channelMetaByChannel[selectedChannelId] || {}
      : listMeta;

  const isInitialLoading = isSearchActive
    ? searchLoading
    : selectedChannelId
      ? channelLoading
      : loading;

  const isLoadingMore = isSearchActive
    ? searchLoadingMore
    : selectedChannelId
      ? channelLoadingMore
      : loadingMore;

  const hasMore = activeMeta.hasMore ?? false;

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || loadMoreLockRef.current) return;
    loadMoreLockRef.current = true;

    const nextPage = (activeMeta.page ?? 0) + 1;
    let promise;

    if (isSearchActive) {
      promise = dispatch(
        searchSavedMessages({
          query: trimmedSearch,
          channelId: selectedChannelId,
          page: nextPage,
          size: SAVED_MESSAGES_PAGE_SIZE,
          append: true,
        })
      );
    } else if (selectedChannelId) {
      promise = dispatch(
        fetchSavedMessagesByChannel({
          channelId: selectedChannelId,
          page: nextPage,
          size: SAVED_MESSAGES_PAGE_SIZE,
          append: true,
        })
      );
    } else {
      promise = dispatch(
        fetchSavedMessages({
          page: nextPage,
          size: SAVED_MESSAGES_PAGE_SIZE,
          append: true,
        })
      );
    }

    promise.finally(() => {
      loadMoreLockRef.current = false;
    });
  }, [
    activeMeta.page,
    dispatch,
    hasMore,
    isLoadingMore,
    isSearchActive,
    selectedChannelId,
    trimmedSearch,
  ]);

  const handleSuggestionSelect = (suggestion) => {
    setSelectedChannelId(suggestion.channelId);
    setSearchQuery(suggestion.name);
  };

  const handleChannelSelect = (channelId) => {
    setSelectedChannelId(channelId);
    setSearchQuery('');
  };

  const displayCount = isSearchActive
    ? searchMeta.totalElements ?? activeMessages.length
    : activeMessages.length;

  if (isInitialLoading && activeMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto mb-4"></div>
          <div className="text-gray-500 dark:text-gray-400">
            {isSearchActive ? 'Searching bookmarks...' : 'Loading bookmarks...'}
          </div>
        </div>
      </div>
    );
  }

  if (error && activeMessages.length === 0) {
    const errorMessage = typeof error === 'string'
      ? error
      : error?.message || error?.toString() || 'Unknown error occurred';
    return (
      <div className="flex items-center justify-center h-full bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="text-red-500 dark:text-red-400 mb-2">Error loading bookmarks</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{errorMessage}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      <SavedMessagesHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        totalCount={totalCount}
        filteredCount={displayCount}
        channelSuggestions={channelSuggestions}
        onSuggestionSelect={handleSuggestionSelect}
      />
      <ChannelFilterChips
        channels={channelChips}
        selectedChannelId={selectedChannelId}
        onSelectChannel={handleChannelSelect}
        totalCount={totalCount}
      />
      <SavedMessageList
        filteredMessages={activeMessages}
        channels={channels}
        directMessages={directMessages}
        getChannelName={getChannelName}
        selectedChannelId={selectedChannelId}
        hasAnySavedMessages={totalCount > 0}
        hasMore={hasMore}
        loadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />
    </div>
  );
};

export default SavedMessagesPanel;
